<template lang="pug">
v-main
  v-container(fluid)
    v-card
      v-toolbar.elevation-0(color="white")
        v-toolbar-title Edit Bank Account (PV)
      v-divider
      v-card-text(v-if="loading")
        v-progress-circular(indeterminate, color="primary")
        span.ml-3 Loading...
      v-card-text(v-else)
        v-form(ref="form", v-model="valid")
          v-row(no-gutters)
            v-col(cols="2") 
              v-subheader(color="black") Business Unit
            v-col(cols="10")
              v-select(
                v-if="!isUsed"
                v-model="formData.bu",
                :items="filteredBUList",
                item-title="business_unit.bu_name",
                item-value="business_unit",
                return-object,
                label="Select Business Unit",
                :rules="[rules.required]",
                required
              )
                template(v-slot:item="data")
                  v-list-item-title {{ getBUName(data.item) }}
                template(v-slot:selection="data")
                  span {{ getBUName(data.item) }}
              v-text-field(
                v-else
                :value="formData.bu && formData.bu.name",
                label="Business Unit",
                readonly,
                disabled
              )

          v-row(no-gutters)
            v-col(cols="2") 
              v-subheader(color="black") Bank
            v-col(cols="10")
              v-autocomplete(
                v-if="!isUsed"
                v-model="formData.bank",
                :items="bankList",
                item-title="text",
                item-value="_id",
                return-object,
                label="Select Bank",
                :rules="[rules.required]",
                :filter="customFilter",
                required
              )
                template(v-slot:item="data")
                  p {{ data.item.text }}
              v-text-field(
                v-else
                :value="formData.bank && formData.bank.name",
                label="Bank",
                readonly,
                disabled
              )

          v-row(no-gutters)
            v-col(cols="2") 
              v-subheader(color="black") Account Name
                span(style="color: #d42a2a") *
            v-col(cols="10")
              v-text-field(
                v-model="formData.account_name",
                label="Account Name",
                :rules="[rules.required]",
                :readonly="isUsed",
                :disabled="isUsed",
                required
              )

          v-row(no-gutters)
            v-col(cols="2") 
              v-subheader(color="black") Account No
                span(style="color: #d42a2a") *
            v-col(cols="10")
              v-text-field(
                v-model="formData.account_no",
                label="Account No",
                :rules="[rules.required, rules.accountNo]",
                :readonly="isUsed",
                :disabled="isUsed",
                required
              )

          v-row(no-gutters)
            v-col(cols="2") 
              v-subheader(color="black") Account Type
                span(style="color: #d42a2a") *
            v-col(cols="10")
              v-select(
                v-model="formData.account_type",
                :items="accountTypeOptions",
                label="Select Account Type",
                :rules="[rules.required]",
                required
              )

          v-row(no-gutters)
            v-col(cols="2") 
              v-subheader(color="black") Currency
                span(style="color: #d42a2a") *
            v-col(cols="10")
              v-select(
                v-model="formData.currency",
                :items="currencyOptions",
                label="Select Currency",
                :rules="[rules.required]",
                required
              )

          v-row(no-gutters)
            v-col(cols="2") 
              v-subheader(color="black") Status
            v-col(cols="10")
              v-radio-group(v-model="formData.is_active", row, :disabled="isUsed")
                v-radio(:label="'Active'", :value="true")
                v-radio(:label="'Non Active'", :value="false")

        v-card-actions.my-8
          v-btn.custom__padding-btn(@click="cancel") Cancel
          v-btn.custom__padding-btn(
            v-if="!isUsed"
            style="background-color:#006bff; color: white",
            @click="submit",
            :disabled="!valid || submitting",
            :loading="submitting"
          ) Save
</template>

<script>
import axios from "axios";

export default {
  name: "PVBankAccountEdit",
  data() {
    return {
      valid: false,
      loading: true,
      submitting: false,
      formData: {
        bu: null,
        bank: null,
        account_name: "",
        account_no: "",
        account_type: null,
        currency: null,
        is_active: true,
      },
      buList: [],
      bankList: [],
      accountTypeOptions: [],
      currencyOptions: [],
      rules: {
        required: (v) => !!v || "This field is required",
        accountNo: (v) => {
          if (!v) return true;
          return /^[a-zA-Z0-9\s\-_]+$/.test(v) || "Account number can only contain alphanumeric characters, spaces, hyphens, and underscores";
        },
      },
      isUsed: false,
    };
  },
  computed: {
    filteredBUList() {
      const seen = {};
      return this.buList
        .filter(item => {
          return item.business_unit &&
                 item.business_unit.is_active === true &&
                 item.is_payment_voucher === true;
        })
        .filter(item => {
          const bu = item.business_unit || {};
          const buId = bu.id || bu._id;
          if (!buId) return false;
          if (seen[buId]) return false;
          seen[buId] = true;
          return true;
        });
    },
  },
  async mounted() {
    await Promise.all([
      this.loadBUList(), 
      this.loadBankList(),
      this.loadAccountTypes(),
      this.loadCurrencies()
    ]);
    this.loadData();
  },
  methods: {
    async loadAccountTypes() {
      const token = localStorage.getItem("token");
      try {
        const res = await axios.get(`${process.env.VUE_APP_API}/pv/account-types`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data.data || [];
        this.accountTypeOptions = data.map(item => item.account_type);
      } catch (err) {
        this.$toasted.error("Failed to load account types", { icon: "error" });
      }
    },
    async loadCurrencies() {
      const token = localStorage.getItem("token");
      try {
        const res = await axios.get(`${process.env.VUE_APP_API}/ref/list-currency`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data || [];
        this.currencyOptions = data.map(item => item.code);
      } catch (err) {
        this.$toasted.error("Failed to load currencies", { icon: "error" });
      }
    },
    async loadBUList() {
      const token = localStorage.getItem("token");
      const userData = this.$store.state.dataUsers;
      const userId = userData.user || userData;
      
      if (userId) {
        try {
          const res = await axios.get(`${process.env.VUE_APP_API}/users/active/bu/${userId._id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          this.buList = res.data.data || [];
        } catch (err) {
          this.$toasted.error("Failed to load business units", { icon: "error" });
        }
      }
    },
    async loadBankList() {
      const token = localStorage.getItem("token");
      try {
        const res = await axios.get(`${process.env.VUE_APP_API}/ref/list-bank`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        this.bankList = res.data || [];
        this.bankList.sort((a, b) => {
          const nameA = a.text ? a.text.toLowerCase() : '';
          const nameB = b.text ? b.text.toLowerCase() : '';
          return nameA.localeCompare(nameB);
        });
      } catch (err) {
        this.$toasted.error("Failed to load bank list", { icon: "error" });
      }
    },
    customFilter(item, queryText, itemText) {
      const textOne = item.text ? item.text.toLowerCase() : "";
      const textTwo = item.alias ? item.alias.toLowerCase() : "";
      const searchText = queryText.toLowerCase();

      return (
        textOne.indexOf(searchText) > -1 || textTwo.indexOf(searchText) > -1
      );
    },
    loadData() {
      const token = localStorage.getItem("token");
      const id = this.$route.params.id;
      
      axios
        .get(`${process.env.VUE_APP_API}/pv/bank-accounts/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          this.isUsed = res.data.is_used || false;
          // Handle backward compatibility: convert old status to is_active
          let isActive = true;
          if (res.data.is_active !== undefined) {
            isActive = res.data.is_active;
          } else if (res.data.status !== undefined) {
            // Convert old status string to boolean
            isActive = res.data.status === 'Active';
          }
          this.formData = {
            bu: res.data.bu,
            bank: res.data.bank,
            account_name: res.data.account_name,
            account_no: res.data.account_no,
            account_type: res.data.account_type || null,
            currency: res.data.currency || null,
            is_active: isActive,
          };
          // If not used, convert bu and bank to editable format
          if (!this.isUsed) {
            // Find matching BU from buList
            if (res.data.bu && res.data.bu.id) {
              const matchingBU = this.buList.find(bu => 
                bu.business_unit && bu.business_unit.id === res.data.bu.id
              );
              if (matchingBU) {
                this.formData.bu = matchingBU;
              }
            }
            // Find matching bank from bankList
            const bankId = res.data.bank && (res.data.bank._id || res.data.bank.id);
            if (bankId) {
              const matchingBank = this.bankList.find(bank => 
                bank._id === bankId || bank.id === bankId
              );
              if (matchingBank) {
                this.formData.bank = matchingBank;
              }
            }
          }
        })
        .catch((err) => {
          this.$toasted.error("Failed to load data", { icon: "error" });
          this.$router.push({ name: "PV Bank Account List" });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submit() {
      if (!this.$refs.form.validate()) return;
      
      this.submitting = true;
      const token = localStorage.getItem("token");
      const id = this.$route.params.id;
      
      const payload = {
        account_name: this.formData.account_name,
        account_no: this.formData.account_no,
        account_type: this.formData.account_type,
        currency: this.formData.currency,
        is_active: this.formData.is_active,
      };
      
      // If not used, allow editing BU and Bank
      if (!this.isUsed) {
        if (this.formData.bu && this.formData.bu.business_unit) {
          payload.bu_id = this.formData.bu.business_unit.id;
          payload.bu_name = this.formData.bu.business_unit.bu_name;
        }
        if (this.formData.bank && this.formData.bank._id) {
          payload.bank_id = this.formData.bank._id;
          payload.bank_name = this.formData.bank.text;
        }
      }
      
      axios
        .put(`${process.env.VUE_APP_API}/pv/bank-accounts/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(() => {
          this.$toasted.success("Success! Bank account has been updated.", { icon: "check_circle" });
          this.$router.push({ name: "PV Bank Account List" });
        })
        .catch((err) => {
          const errorMsg = err.response && err.response.data && err.response.data.message || "Failed to update bank account";
          this.$toasted.error(errorMsg, { icon: "error" });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    getBUName(item) {
      return item.business_unit && item.business_unit.bu_name ? item.business_unit.bu_name : '';
    },
    cancel() {
      this.$router.push({ name: "PV Bank Account List" });
    },
  },
};
</script>

<style scoped>
.custom__padding-btn {
  margin-right: 0.5rem;
}
</style>
